import { action, observable } from 'mobx';
import {
  checkBopisAvailabilityByCartForSidebar,
  checkBopisAvailabilityByProducts,
  extractStoresDataFromBopisProductAvailabilityData,
} from './bopisUtils';
import { IBopisState } from './bopisTypes';
import { IVariant } from '../../types/PDP';
import { getFisOrBopisFeatureFlagStatus, mapCartItemsToBopisAndSddParamItems } from '../../utils';
import { IStoreAvailabilityData } from '../StoreLocator/storeLocatorTypes';
import { isEmpty } from 'lodash-es';
import { setStoreLocatorSidebarIsLoadingState } from '../StoreLocator/storeLocatorSidebarState';

const LOCAL_STORAGE_KEY_BOPIS_STORE = 'alo-users-preferred-bopis-store';
const selectedStoreFromLS = localStorage.getItem(LOCAL_STORAGE_KEY_BOPIS_STORE);

export const bopisState = observable<IBopisState>({
  enabled: getFisOrBopisFeatureFlagStatus('bopis'),
  storeSelection: selectedStoreFromLS ? JSON.parse(selectedStoreFromLS) : null,
  pdpStoresData: null,
  pdpStoresDataError: null,
  cartStoresData: null,
  cartStoresDataError: null,
  cartProductsAvailability: null,
  cartProductsAvailabilityError: null,
  isFetchingData: false,
  async fetchStoresDataPdp(selectedVariant: IVariant, pdpSingleTileFetch = false, isGWP = false) {
    const { sku, requiresShipping } = selectedVariant;
    const products = [
      {
        sku: sku,
        quantity: 1,
        isGWP,
        requires_shipping: requiresShipping,
      },
    ];
    // on initial pdp fetch or when selected sku updates, return just the specified store data
    const shouldFetchSelectedStoreDataOnly = pdpSingleTileFetch && !isEmpty(bopisState.storeSelection);
    setStoreLocatorSidebarIsLoadingState(true);
    const response = await checkBopisAvailabilityByProducts(products, {
      shouldUseLocationId: shouldFetchSelectedStoreDataOnly,
      includeInStoreData: true,
    });
    setStoreLocatorSidebarIsLoadingState(false);
    if ('detail' in response) {
      console.log({ error: response.detail });
      if (response.detail.toLowerCase() === 'location is unknown') return;
      this.pdpStoresDataError = response;
    } else {
      if (this.pdpStoresDataError) this.pdpStoresDataError = null;
      const storesData = extractStoresDataFromBopisProductAvailabilityData(response);
      this.pdpStoresData = response;
      if (storesData.length > 0 && storesData[0] && isEmpty(bopisState.storeSelection)) {
        // on first successful response, set first index as selected store
        setStoreSelection([storesData[0]]);
      } else if (storesData.length === 0 && isEmpty(bopisState.storeSelection)) {
        // if no stores returned, set selection to empty array to render no store copy to user
        setStoreSelection([]);
      } else if (shouldFetchSelectedStoreDataOnly && storesData[0]) {
        // if store is selected, refresh store selection data on initial pdp data fetch
        setStoreSelection([storesData[0]]);
      }
    }
  },
  async fetchStoresDataCart(cartData) {
    if (this.cartStoresData) this.cartStoresData = null; // Reset the storesData to show the loading state inside the sidebar
    const mappedItems = mapCartItemsToBopisAndSddParamItems(cartData);
    setStoreLocatorSidebarIsLoadingState(true);
    const response = await checkBopisAvailabilityByCartForSidebar(mappedItems);
    setStoreLocatorSidebarIsLoadingState(false);
    if ('detail' in response) {
      console.log({ error: response.detail });
      this.cartStoresDataError = response;
      if (response.detail.toLowerCase() === 'location is unknown') return;
    } else {
      if (this.cartStoresDataError) this.cartStoresDataError = null;
      this.cartStoresData = response;
    }
  },
  async fetchAvailableProductsCart(cartData) {
    const mappedItems = mapCartItemsToBopisAndSddParamItems(cartData);
    const response = await checkBopisAvailabilityByProducts(mappedItems, { shouldUseLocationId: true });
    if ('detail' in response) {
      console.log({ error: response.detail });
      if (response.detail.toLowerCase() === 'location is unknown') return;
      this.cartProductsAvailabilityError = response;
    } else {
      if (this.cartProductsAvailabilityError) this.cartProductsAvailabilityError = null;
      const cartProductsAvailability = response;
      this.cartProductsAvailability = cartProductsAvailability;
    }
  },
});

export const setStoreSelection = action((store: IStoreAvailabilityData[]) => {
  bopisState.storeSelection = store;
  localStorage.setItem(LOCAL_STORAGE_KEY_BOPIS_STORE, JSON.stringify(store));
});

export const setIsFetchingData = action((newValue: boolean) => {
  bopisState.isFetchingData = newValue;
});
